import { forwardRef, type HTMLAttributes } from "react";
import { EyeOff } from "lucide-react";
import type { TileConfig } from "../types";
import type { WidgetId } from "@/other/constants/EntityManagerConfig";
import { TILE_COLORS } from "../constants";
import { cn } from "@/modern/utils";

interface ConfiguratorTileProps extends HTMLAttributes<HTMLDivElement> {
  tile: TileConfig;
  onToggleVisibility: (id: WidgetId) => void;
}

/**
 * Visible-grid tile body. Drag and resize are handled by react-grid-layout —
 * this component only renders the visual chrome (color, title, hide button).
 *
 * The `forwardRef` and pass-through props let react-grid-layout attach its
 * own ref/listeners/styles when the tile is used as a `GridLayout` child.
 */
export const ConfiguratorTile = forwardRef<HTMLDivElement, ConfiguratorTileProps>(
  function ConfiguratorTile(
    { tile, onToggleVisibility, children, className, style, ...rest },
    ref
  ) {
    const bgColor = TILE_COLORS[tile.color] ?? TILE_COLORS.blue;

    return (
      <div
        ref={ref}
        style={style}
        className={cn(
          "relative group rounded-md overflow-hidden select-none h-full",
          "hover:ring-1 hover:ring-white/30 transition-shadow",
          className
        )}
        {...rest}
      >
        <div
          className="absolute inset-0 rounded-md pointer-events-none"
          style={{ backgroundColor: bgColor, opacity: 0.72 }}
        />

        <div className="relative h-full flex items-center justify-center px-4">
          <h3 className="text-sm font-medium text-white/90 tracking-wide">
            {tile.title}
          </h3>
        </div>

        <button
          type="button"
          data-tile-action
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility(tile.id);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-80 transition-opacity p-1.5 rounded hover:bg-black/20 z-20"
        >
          <EyeOff className="w-4 h-4 text-white/70" />
        </button>

        {children}
      </div>
    );
  }
);
